import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const SYMBOLS = {
  PLN: 'zł', EUR: '€', USD: '$', GBP: '£',
  CZK: 'Kč', UAH: '₴', RUB: '₽', CHF: 'Fr',
  SEK: 'kr', NOK: 'kr', DKK: 'kr', HUF: 'Ft',
  RON: 'lei', BGN: 'лв',
};

const FALLBACK_RATES = {
  PLN: 1.0, EUR: 0.23, USD: 0.25, GBP: 0.20,
  CZK: 5.70, UAH: 10.5, RUB: 22.0, CHF: 0.22,
};

const CurrencyContext = createContext({});

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => localStorage.getItem('currency') || 'PLN');
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [country, setCountry] = useState('');
  const [detected, setDetected] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/geo/currency`)
      .then(r => r.json())
      .then(data => {
        if (data?.rates) setRates(data.rates);
        if (data?.country) setCountry(data.country);
        if (data?.currency) {
          setDetected(data.currency);
          // Only auto-set when user never picked and geo currency is present in rates
          const userOverride = localStorage.getItem('currency');
          if (!userOverride && data.rates?.[data.currency]) {
            setCurrencyState(data.currency);
          }
        }
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    localStorage.setItem('currency', c);
  }, []);

  const convertFromPLN = useCallback((plnAmount) => {
    const rate = rates[currency] ?? 1;
    return Number(plnAmount || 0) * rate;
  }, [currency, rates]);

  /** Local amount (in current currency) → PLN. Used for price-range filters
   * where the user types a local number but the DB stores PLN. */
  const convertToPLN = useCallback((localAmount) => {
    const rate = rates[currency] ?? 1;
    if (!rate) return Number(localAmount || 0);
    return Number(localAmount || 0) / rate;
  }, [currency, rates]);

  const formatPrice = useCallback((plnAmount, opts = {}) => {
    const { showSymbol = true, decimals } = opts;
    const value = convertFromPLN(plnAmount);
    const d = decimals ?? (value >= 100 ? 0 : value >= 10 ? 1 : 2);
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
    return showSymbol ? `${formatted} ${SYMBOLS[currency] || currency}` : formatted;
  }, [convertFromPLN, currency]);

  const symbol = SYMBOLS[currency] || currency;
  const supportedCurrencies = useMemo(() => Object.keys(rates), [rates]);

  return (
    <CurrencyContext.Provider value={{
      currency, setCurrency, rates, country, detected, ready, symbol,
      formatPrice, convertFromPLN, convertToPLN, supportedCurrencies, SYMBOLS,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
